import { useState, useMemo, useEffect, useRef, useLayoutEffect, Fragment } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { ExportMenu } from '@/components/common/ExportMenu';
import { KpiStrip } from '@/components/common/KpiStrip';
import { FilterChip } from '@/components/common/FilterChip';
import { PageLoader } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  ChevronsUpDown,
  ChevronUp,
  ChevronDown,
  X,
  Search,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Key,
  Clock,
  AlertTriangle,
  Users as UsersIcon,
} from '@/utils/icons';
import { TablePagination } from '@/components/common/TablePagination';
import { useUserSummary, useUsers, useUserDetail, useUserRoles } from '@/hooks/useUsers';
import { Badge } from '@/components/ui/badge';
import type { RolePermissionsItem } from '@/types/user';
import {
  createBrandedPdf,
  addMetricRow,
  addSectionHeading,
  addBrandedTable,
  downloadPdf,
  loadLogoForPdf,
  exportFilename,
} from '@/utils/pdf';
import { downloadCsv, formatExportDate } from '@/utils/csv';
import { renderPieLabel, renderActiveShape } from '@/utils/calculations';
import { PIE_COLORS } from '@/utils/chart-colors';
import { CORE, SCALES, BRAND, STATUS } from '@/utils/dls-tokens';
import type { UserDetail } from '@/types/user';

const RECENCY_COLORS: Record<string, string> = {
  Today: CORE.neeveGreen,
  'Last 7 days': SCALES.green[700],
  'Last 30 days': BRAND.tertiary,
  'Last 90 days': SCALES.amber[300],
  '90+ days': SCALES.orange[400],
  Never: STATUS.danger.primary,
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const ms = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(ms / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return '1 month ago';
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(months / 12);
  return `${years}y ${months % 12}m ago`;
}

function FlagBadge({ flag }: { flag: string }) {
  const isHighRisk =
    flag === 'Password Expired' || flag.includes('Overdue') || flag === 'Disabled';
  return (
    <span
      className={cn(
        'dls-inline-flex dls-items-center dls-rounded-full dls-px-2 dls-py-0.5 dls-text-xs dls-font-medium dls-whitespace-nowrap',
        isHighRisk ? 'dls-bg-status-offline-quarternary dls-text-status-offline' : 'dls-bg-status-warning-quarternary dls-text-status-warning'
      )}
    >
      {flag}
    </span>
  );
}

/**
 * User Detail Drawer — slides in from right when a user row is clicked.
 */
function UserDetailDrawer({
  userId,
  onClose,
  roles,
}: {
  userId: string;
  onClose: () => void;
  roles: RolePermissionsItem[];
}) {
  const { detail, loading } = useUserDetail(userId);

  return (
    <div className="dls-fixed dls-inset-0 dls-z-50 dls-flex dls-justify-end">
      {/* Backdrop */}
      <div className="dls-absolute dls-inset-0 dls-bg-black/40" onClick={onClose} />
      {/* Drawer panel */}
      <div className="dls-relative dls-w-full dls-max-w-lg dls-bg-background dls-shadow-50 dls-overflow-y-auto">
        <div className="dls-sticky dls-top-0 dls-z-10 dls-bg-background dls-border-b dls-px-6 dls-py-4 dls-flex dls-items-center dls-justify-between">
          <h2 className="dls-text-lg dls-font-semibold">User Details</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="dls-h-4 dls-w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="dls-flex dls-items-center dls-justify-center dls-py-20">
            <div className="dls-animate-spin dls-rounded-full dls-h-8 dls-w-8 dls-border-b-2 dls-border-primary" />
          </div>
        ) : detail ? (
          <UserDetailContent detail={detail} roles={roles} />
        ) : (
          <div className="dls-px-6 dls-py-8 dls-text-center dls-text-muted-foreground">User not found.</div>
        )}
      </div>
    </div>
  );
}

function UserDetailContent({ detail, roles }: { detail: UserDetail; roles: RolePermissionsItem[] }) {
  const rolePerms = useMemo(
    () => roles.find((r) => r.name === detail.role) ?? null,
    [roles, detail.role]
  );

  return (
    <div className="dls-px-6 dls-py-4 dls-space-y-6">
      {/* Identity */}
      <div className="dls-space-y-3">
        <h3 className="dls-text-2xl dls-font-bold">{detail.name}</h3>
        <div className="dls-text-sm dls-text-muted-foreground dls-space-y-1">
          <p>{detail.email}</p>
          {detail.username && detail.username !== detail.email && (
            <p>Username: {detail.username}</p>
          )}
          <p>
            Role:{' '}
            <span className="dls-font-medium dls-text-foreground">{detail.role || 'None'}</span>
          </p>
        </div>
      </div>

      {/* Flags */}
      {detail.flags.length > 0 && (
        <div className="dls-space-y-2">
          <h4 className="dls-text-sm dls-font-semibold dls-flex dls-items-center dls-gap-1.5">
            <ShieldAlert className="dls-h-4 dls-w-4 dls-text-status-offline" />
            Security Flags
          </h4>
          <div className="dls-flex dls-flex-wrap dls-gap-1.5">
            {detail.flags.map((flag) => (
              <FlagBadge key={flag} flag={flag} />
            ))}
          </div>
          {detail.suggestedActions.length > 0 && (
            <ul className="dls-text-xs dls-text-muted-foreground dls-space-y-1 dls-pl-4 dls-list-disc">
              {detail.suggestedActions.map((action, i) => (
                <li key={i}>{action}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Status details */}
      <div className="dls-grid dls-grid-cols-2 dls-gap-3 dls-text-sm">
        <div>
          <span className="dls-text-muted-foreground">Enabled</span>
          <p className="dls-font-medium">
            {detail.enabled === true ? (
              <span className="dls-text-status-online">Yes</span>
            ) : detail.enabled === false ? (
              <span className="dls-text-status-offline">No</span>
            ) : (
              '\u2014'
            )}
          </p>
        </div>
        <div>
          <span className="dls-text-muted-foreground">Email Verified</span>
          <p className="dls-font-medium">
            {detail.emailVerified === true ? (
              <span className="dls-text-status-online">Yes</span>
            ) : detail.emailVerified === false ? (
              <span className="dls-text-status-offline">No</span>
            ) : (
              '\u2014'
            )}
          </p>
        </div>
        <div>
          <span className="dls-text-muted-foreground">Last Login</span>
          <p className="dls-font-medium">{timeAgo(detail.lastLogin)}</p>
          {detail.lastLogin && (
            <p className="dls-text-xs dls-text-muted-foreground">{formatDateTime(detail.lastLogin)}</p>
          )}
        </div>
        <div>
          <span className="dls-text-muted-foreground">Password Changed</span>
          <p className="dls-font-medium">{timeAgo(detail.passwordChangedAt)}</p>
        </div>
        <div>
          <span className="dls-text-muted-foreground">Password Expires</span>
          <p className="dls-font-medium">{formatDate(detail.passwordExpiredAt)}</p>
        </div>
        <div>
          <span className="dls-text-muted-foreground">Account Created</span>
          <p className="dls-font-medium">{formatDate(detail.createdAt)}</p>
        </div>
      </div>

      {/* API Keys */}
      {detail.apiKeys.length > 0 && (
        <div className="dls-space-y-2">
          <h4 className="dls-text-sm dls-font-semibold dls-flex dls-items-center dls-gap-1.5">
            <Key className="dls-h-4 dls-w-4" />
            API Keys ({detail.apiKeys.length})
          </h4>
          <div className="dls-space-y-2">
            {detail.apiKeys.map((key) => (
              <div
                key={key.id}
                className="dls-border dls-rounded-lg dls-p-3 dls-text-sm dls-flex dls-items-center dls-justify-between"
              >
                <div>
                  <p className="dls-font-medium">{key.name}</p>
                  <p className="dls-text-xs dls-text-muted-foreground dls-font-mono">{key.prefix}...</p>
                </div>
                <div className="dls-text-right dls-text-xs dls-text-muted-foreground">
                  <p>Created: {formatDate(key.createdAt)}</p>
                  <p>Last used: {key.lastUsedAt ? timeAgo(key.lastUsedAt) : 'Never'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {detail.recentActivity.length > 0 && (
        <div className="dls-space-y-2">
          <h4 className="dls-text-sm dls-font-semibold dls-flex dls-items-center dls-gap-1.5">
            <Clock className="dls-h-4 dls-w-4" />
            Recent Activity ({detail.recentActivity.length})
          </h4>
          <div className="dls-space-y-1.5 dls-max-h-80 dls-overflow-y-auto">
            {detail.recentActivity.slice(0, 20).map((act) => (
              <div key={act.id} className="dls-border dls-rounded-lg dls-p-2.5 dls-text-xs">
                <div className="dls-flex dls-items-center dls-justify-between">
                  <span className="dls-font-medium dls-text-foreground">
                    {act.type.replace(/_/g, ' ')}
                  </span>
                  <span className="dls-text-muted-foreground">{formatDateTime(act.occurredAt)}</span>
                </div>
                {act.description && (
                  <p className="dls-text-muted-foreground dls-mt-0.5 dls-line-clamp-2">{act.description}</p>
                )}
                {(act.ipAddress || act.clientInfo) && (
                  <p className="dls-text-muted-foreground/70 dls-mt-0.5">
                    {[act.ipAddress, act.clientInfo].filter(Boolean).join(' \u2022 ')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Role Permissions */}
      {rolePerms && rolePerms.permissions.length > 0 && (
        <div className="dls-space-y-2">
          <h4 className="dls-text-sm dls-font-semibold dls-flex dls-items-center dls-gap-1.5">
            <Key className="dls-h-4 dls-w-4" />
            Permissions – {rolePerms.name}
          </h4>
          <div className="dls-space-y-3">
            {rolePerms.permissions.map((cat) => (
              <div key={cat.category}>
                <p className="dls-text-[10px] dls-font-semibold dls-uppercase dls-tracking-wider dls-text-muted-foreground dls-mb-1">
                  {cat.category}
                </p>
                <ul className="dls-text-xs dls-space-y-1">
                  {cat.actions.map((a) => (
                    <li key={a.action} className="dls-flex dls-items-baseline dls-gap-2">
                      <span className="dls-font-medium dls-shrink-0">{a.action}</span>
                      {a.description && (
                        <span className="dls-text-muted-foreground">{a.description}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Org Security Policy card.
 */
function OrgSecurityPolicyCard({
  policy,
}: {
  policy: NonNullable<import('@/types/user').UserPageSummary['orgSecurityPolicy']>;
}) {
  const items = [
    {
      label: 'Password Expiry',
      value: policy.expirePasswordInDays ? `${policy.expirePasswordInDays} days` : 'Not set',
      ok: policy.expirePasswordInDays != null && policy.expirePasswordInDays <= 90,
    },
    {
      label: 'Max Login Failures',
      value: policy.maxAllowedLoginFailures != null ? String(policy.maxAllowedLoginFailures) : 'Not set',
      ok: policy.maxAllowedLoginFailures != null && policy.maxAllowedLoginFailures <= 5,
    },
    {
      label: 'Reuse Prevention',
      value: policy.passwordReuseCount != null ? `Last ${policy.passwordReuseCount}` : 'Not set',
      ok: policy.passwordReuseCount != null && policy.passwordReuseCount >= 3,
    },
    {
      label: 'Session Timeout',
      value: policy.sessionTimeoutMinutes != null ? `${policy.sessionTimeoutMinutes} min` : 'Not set',
      ok: policy.sessionTimeoutMinutes != null && policy.sessionTimeoutMinutes <= 60,
    },
    {
      label: '2FA Enabled',
      value: policy.twoFactorEnabled === true ? 'Yes' : policy.twoFactorEnabled === false ? 'No' : 'Unknown',
      ok: policy.twoFactorEnabled === true,
    },
    {
      label: '2FA Forced',
      value: policy.twoFactorForced === true ? 'Yes' : policy.twoFactorForced === false ? 'No' : 'Unknown',
      ok: policy.twoFactorForced === true,
    },
    {
      label: 'API Keys Enabled',
      value: policy.apiKeyEnabled === true ? 'Yes' : policy.apiKeyEnabled === false ? 'No' : 'Unknown',
      ok: null as boolean | null, // neutral
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
          <Shield className="dls-h-4 dls-w-4" />
          Organization Security Policy
          <InfoTooltip text="Security policies configured in the Neeve portal for this organization. Green indicates recommended settings are met." />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="dls-grid dls-grid-cols-2 sm:dls-grid-cols-3 lg:dls-grid-cols-4 dls-gap-x-4 dls-gap-y-3">
          {items.map((item) => (
            <div key={item.label} className="dls-text-sm">
              <p className="dls-text-muted-foreground dls-text-xs dls-h-4 dls-leading-4 dls-truncate" title={item.label}>{item.label}</p>
              <p className="dls-font-medium dls-flex dls-items-center dls-gap-1.5 dls-mt-1">
                {item.ok === true && <ShieldCheck className="dls-h-3.5 dls-w-3.5 dls-text-status-online dls-shrink-0" />}
                {item.ok === false && <AlertTriangle className="dls-h-3.5 dls-w-3.5 dls-text-status-warning dls-shrink-0" />}
                {item.value}
              </p>
            </div>
          ))}
        </div>
        {policy.passwordRules.length > 0 && (
          <div className="dls-mt-3 dls-pt-3 dls-border-t">
            <p className="dls-text-xs dls-text-muted-foreground dls-mb-1.5">Password Rules</p>
            <ul className="dls-text-xs dls-text-muted-foreground dls-space-y-0.5 dls-list-disc dls-pl-4">
              {policy.passwordRules.map((rule, i) => (
                <li key={i}>{rule.message}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Roles & Permissions expandable-row card.
 */
function RolesPermissionsCard({ roles, loading }: { roles: RolePermissionsItem[]; loading: boolean }) {
  const [expandedRole, setExpandedRole] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
          <Key className="dls-h-4 dls-w-4" />
          Roles & Permissions
          <InfoTooltip text="Permissions assigned to each role in the organization. Expand a role to see its permission categories and actions." />
        </CardTitle>
      </CardHeader>
      <CardContent className="dls-p-0">
        {loading ? (
          <div className="dls-text-center dls-py-12">
            <div className="dls-inline-block dls-animate-spin dls-rounded-full dls-h-6 dls-w-6 dls-border-b-2 dls-border-primary" />
          </div>
        ) : roles.length === 0 ? (
          <p className="dls-text-sm dls-text-muted-foreground dls-text-center dls-py-12">
            No role data available.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="dls-w-10" />
                <TableHead>Role</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="dls-text-right">Users</TableHead>
                <TableHead className="dls-text-right">Permissions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((role) => (
                <Fragment key={role.name}>
                  <TableRow
                    className="dls-cursor-pointer hover:dls-bg-muted/50"
                    onClick={() =>
                      setExpandedRole(expandedRole === role.name ? null : role.name)
                    }
                  >
                    <TableCell className="dls-w-10 dls-px-3">
                      <ChevronDown
                        className={cn(
                          'dls-h-4 dls-w-4 dls-text-muted-foreground dls-transition-transform',
                          expandedRole === role.name && 'dls-rotate-180'
                        )}
                      />
                    </TableCell>
                    <TableCell className="dls-font-medium">{role.name}</TableCell>
                    <TableCell>
                      <Badge variant={role.custom ? 'outline' : 'secondary'}>
                        {role.custom ? 'Custom' : 'Default'}
                      </Badge>
                    </TableCell>
                    <TableCell className="dls-text-right">{role.userCount}</TableCell>
                    <TableCell className="dls-text-right">{role.permissionCount}</TableCell>
                  </TableRow>
                  {expandedRole === role.name && role.permissions.length > 0 && (
                    <TableRow key={`${role.name}-perms`}>
                      <TableCell colSpan={5} className="dls-bg-muted/30 dls-p-0">
                        <div className="dls-px-6 dls-py-3">
                          <div className="dls-grid dls-grid-cols-2 md:dls-grid-cols-3 lg:dls-grid-cols-4 dls-gap-4">
                            {role.permissions.map((cat) => (
                              <div key={cat.category}>
                                <p className="dls-text-[10px] dls-font-semibold dls-uppercase dls-tracking-wider dls-text-muted-foreground dls-mb-1">
                                  {cat.category}
                                </p>
                                <ul className="dls-text-xs dls-space-y-0.5">
                                  {cat.actions.map((a) => (
                                    <li key={a.action} title={a.description} className="dls-cursor-default">
                                      {a.action}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export function Users() {
  const { summary, loading: summaryLoading } = useUserSummary();
  const { roles, loading: rolesLoading } = useUserRoles();

  // Chart hover state
  const [activeRoleIndex, setActiveRoleIndex] = useState<number | undefined>(undefined);

  // Table state
  const [roleFilter, setRoleFilter] = useState('');
  const [flagFilter, setFlagFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Drawer
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const {
    users,
    total,
    totalPages,
    loading: usersLoading,
  } = useUsers({
    role: roleFilter || undefined,
    flag: flagFilter || undefined,
    enabled: enabledFilter || undefined,
    search: debouncedSearch || undefined,
    page,
    limit,
    sortBy,
    sortOrder,
  });

  // Stabilize table wrapper height to prevent page jump during search/filter
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const stableHeightRef = useRef<number>(0);

  useLayoutEffect(() => {
    if (!tableWrapperRef.current) return;
    if (!usersLoading && users.length >= limit) {
      stableHeightRef.current = tableWrapperRef.current.offsetHeight;
      tableWrapperRef.current.style.minHeight = '';
    } else if (stableHeightRef.current > 0) {
      tableWrapperRef.current.style.minHeight = `${stableHeightRef.current}px`;
    }
  }, [users, usersLoading, limit]);

  const handleFilterChange = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  };

  // Unique roles from summary for filter dropdown
  const availableRoles = useMemo(
    () => (summary?.roleDistribution ?? []).map((r) => r.role).sort(),
    [summary]
  );

  // Unique flags from summary for filter dropdown
  const availableFlags = useMemo(
    () => (summary?.securityFlags ?? []).map((f) => f.flag).sort(),
    [summary]
  );

  const hasFilters = !!(debouncedSearch || roleFilter || flagFilter || enabledFilter);

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setRoleFilter('');
    setFlagFilter('');
    setEnabledFilter('');
    setPage(1);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortBy !== field)
      return <ChevronsUpDown className="dls-h-3.5 dls-w-3.5 dls-text-muted-foreground/50" />;
    return sortOrder === 'asc' ? (
      <ChevronUp className="dls-h-3.5 dls-w-3.5" />
    ) : (
      <ChevronDown className="dls-h-3.5 dls-w-3.5" />
    );
  };

  const SortableHeader = ({
    field,
    children,
    className = '',
  }: {
    field: string;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => handleSort(field)}
        className={`dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors dls-px-1 dls-py-0.5 dls-rounded ${className?.includes('text-center') ? 'mx-auto' : '-ml-1'}`}
      >
        {children}
        <SortIcon field={field} />
      </button>
    </TableHead>
  );

  const handleExportPdf = async () => {
    if (!summary) return;

    const logoData = await loadLogoForPdf();
    const layout = createBrandedPdf({
      title: 'User Security Report',
      subtitle: `${summary.totalUsers} users`,
      logoData,
    });

    let y = addMetricRow(
      layout.doc,
      [
        { label: 'Total Users', value: String(summary.totalUsers) },
        { label: 'Enabled', value: String(summary.enabledCount) },
        { label: 'Disabled', value: String(summary.disabledCount) },
        { label: 'Flagged', value: String(summary.flaggedCount) },
        { label: 'Never Logged In', value: String(summary.neverLoggedInCount) },
        { label: 'Password Expired', value: String(summary.passwordExpiredCount) },
      ],
      layout.startY,
      layout
    );

    // Security flags table
    if (summary.securityFlags.length > 0) {
      y = addSectionHeading(layout.doc, 'Security Flag Breakdown', y + 6, layout);
      y = addBrandedTable(
        layout.doc,
        ['Flag', 'Count'],
        summary.securityFlags.map((f) => [f.flag, f.count]),
        y,
        layout
      );
    }

    // Role distribution table
    if (summary.roleDistribution.length > 0) {
      y = addSectionHeading(layout.doc, 'Role Distribution', y + 6, layout);
      y = addBrandedTable(
        layout.doc,
        ['Role', 'User Count'],
        summary.roleDistribution.map((r) => [r.role, r.count]),
        y,
        layout
      );
    }

    // User list table
    if (users.length > 0) {
      y = addSectionHeading(layout.doc, 'User List', y + 6, layout);
      addBrandedTable(
        layout.doc,
        ['Name', 'Email', 'Role', 'Last Login', 'Flags'],
        users.map((u) => [
          u.name,
          u.email,
          u.role || '',
          u.lastLogin ? formatDate(u.lastLogin) : 'Never',
          u.flags.join(', '),
        ]),
        y,
        layout
      );
    }

    downloadPdf(layout.doc, exportFilename('users', 'pdf'));
  };

  const handleExportCsv = () => {
    if (!users.length) return;

    downloadCsv(
      users.map((u) => ({
        Name: u.name,
        Email: u.email,
        Username: u.username || '',
        Role: u.role || '',
        Enabled: u.enabled === true ? 'Yes' : u.enabled === false ? 'No' : '',
        'Email Verified': u.emailVerified === true ? 'Yes' : u.emailVerified === false ? 'No' : '',
        'Last Login': formatExportDate(u.lastLogin),
        'Password Changed': formatExportDate(u.passwordChangedAt),
        'Password Expires': formatExportDate(u.passwordExpiredAt),
        'Created At': formatExportDate(u.createdAt),
        Flags: u.flags.join('; '),
      })),
      exportFilename('users', 'csv'),
      { report: 'Users' }
    );
  };

  if (summaryLoading) {
    return <PageLoader />;
  }

  return (
    <div className="dls-space-y-6">
      {/* Header */}
      <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
        <div>
          <h1 className="dls-page-title dls-flex dls-items-center dls-gap-2"><UsersIcon className="dls-h-7 dls-w-7 dls-text-neeve-primary" />Users</h1>
          <p className="dls-text-muted-foreground">
            User accounts, security posture, and access management insights
          </p>
        </div>
        <ExportMenu
          onExportPdf={handleExportPdf}
          onExportCsv={handleExportCsv}
          label="Export Users"
        />
      </div>

      {/* KPI Strip */}
      {summary && (
        <KpiStrip
          items={[
            { label: 'Total Users', value: summary.totalUsers, tooltip: 'Total number of user accounts in the Neeve portal.' },
            { label: 'Enabled', value: summary.enabledCount, variant: 'success', tooltip: 'Active user accounts.' },
            { label: 'Flagged', value: summary.flaggedCount, variant: summary.flaggedCount > 0 ? 'danger' : 'default', tooltip: 'Users with at least one security flag (expired password, inactive, disabled, unverified email, etc.).' },
            { label: 'Never Logged In', value: summary.neverLoggedInCount, variant: summary.neverLoggedInCount > 0 ? 'warning' : 'default', tooltip: 'Accounts that have never been used.' },
            { label: 'Inactive', value: summary.inactiveCount, variant: summary.inactiveCount > 0 ? 'warning' : 'default', tooltip: "Users who haven't logged in within the policy threshold (1/3 of password expiry days)." },
            { label: 'Password Expired', value: summary.passwordExpiredCount, variant: summary.passwordExpiredCount > 0 ? 'danger' : 'default', tooltip: 'Users with expired passwords who have previously logged in.' },
          ]}
        />
      )}

      {/* Charts Row: Role Distribution + Login Recency */}
      {summary && (
        <div className="dls-grid dls-gap-6 lg:dls-grid-cols-2">
          {/* Role Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
                Role Distribution
                <InfoTooltip text="Number of users assigned to each role in the organization." />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {summary.roleDistribution.length === 0 ? (
                <p className="dls-text-sm dls-text-muted-foreground dls-text-center dls-py-8">
                  No role data available.
                </p>
              ) : (
                <div className="dls-h-[280px] dls-flex dls-items-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={summary.roleDistribution}
                        dataKey="count"
                        nameKey="role"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        label={renderPieLabel}
                        labelLine={false}
                        activeIndex={activeRoleIndex}
                        activeShape={renderActiveShape}
                        onMouseEnter={(_, index) => setActiveRoleIndex(index)}
                        onMouseLeave={() => setActiveRoleIndex(undefined)}
                      >
                        {summary.roleDistribution.map((_, index) => (
                          <Cell key={`role-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number, name: string) => [`${value} users`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Login Recency */}
          <Card>
            <CardHeader>
              <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
                Login Recency
                <InfoTooltip text="Distribution of users by how recently they last logged in." />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="dls-h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.loginRecency}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="bucket"
                      fontSize={11}
                      tickFormatter={(v) => (v.length > 10 ? v.replace('Last ', '') : v)}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip formatter={(value: number) => [`${value} users`, 'Users']} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={32}>
                      {summary.loginRecency.map((entry, index) => (
                        <Cell
                          key={`login-${index}`}
                          fill={RECENCY_COLORS[entry.bucket] ?? CORE.neeveGreen}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

        </div>
      )}

      {/* Security Flags Breakdown + Org Security Policy */}
      {summary && (
        <div className="dls-grid dls-gap-6 lg:dls-grid-cols-2">
          {/* Security Flags */}
          <Card>
            <CardHeader>
              <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
                <ShieldAlert className="dls-h-4 dls-w-4 dls-text-status-offline" />
                Security Flag Breakdown
                <InfoTooltip text="Count of users with each type of security flag. A user may have multiple flags." />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {summary.securityFlags.length === 0 ? (
                <div className="dls-flex dls-items-center dls-gap-2 dls-py-4">
                  <ShieldCheck className="dls-h-5 dls-w-5 dls-text-status-online" />
                  <p className="dls-text-sm dls-text-muted-foreground">
                    No security flags detected. All users are in good standing.
                  </p>
                </div>
              ) : (
                <div className="dls-space-y-2">
                  {summary.securityFlags.map((item) => {
                    const pct = summary.totalUsers > 0 ? (item.count / summary.totalUsers) * 100 : 0;
                    const isHigh = item.flag === 'Password Expired' || item.flag.includes('Overdue');
                    return (
                      <div key={item.flag} className="dls-flex dls-items-center dls-gap-3">
                        <div className="dls-w-56 dls-shrink-0 dls-text-sm dls-font-medium dls-truncate" title={item.flag}>{item.flag}</div>
                        <div className="dls-flex-1 dls-h-5 dls-bg-muted dls-rounded-full dls-overflow-hidden">
                          <div
                            className={cn(
                              'dls-h-full dls-rounded-full dls-transition-all',
                              isHigh ? 'dls-bg-status-offline' : 'dls-bg-status-warning'
                            )}
                            style={{ width: `${Math.max(pct, 2)}%` }}
                          />
                        </div>
                        <div className="dls-w-16 dls-text-sm dls-text-right dls-text-muted-foreground">
                          {item.count} ({pct.toFixed(0)}%)
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Org Security Policy */}
          {summary.orgSecurityPolicy && (
            <OrgSecurityPolicyCard policy={summary.orgSecurityPolicy} />
          )}
        </div>
      )}

      {/* Roles & Permissions */}
      <RolesPermissionsCard roles={roles} loading={rolesLoading} />

      {/* User Table */}
      <div ref={tableWrapperRef}>
      <Card>
        <CardHeader>
          <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
            <CardTitle>User Directory</CardTitle>
            <div className="dls-flex dls-items-center dls-gap-2 dls-flex-wrap">
              {debouncedSearch && (
                <FilterChip label={`Search: "${debouncedSearch}"`} onRemove={() => { setSearch(''); setDebouncedSearch(''); setPage(1); }} />
              )}
              {roleFilter && (
                <FilterChip label={`Role: ${roleFilter}`} onRemove={() => { setRoleFilter(''); setPage(1); }} />
              )}
              {enabledFilter && (
                <FilterChip label={`Status: ${enabledFilter === 'true' ? 'Enabled' : 'Disabled'}`} onRemove={() => { setEnabledFilter(''); setPage(1); }} />
              )}
              {flagFilter && (
                <FilterChip label={`Flag: ${flagFilter}`} onRemove={() => { setFlagFilter(''); setPage(1); }} />
              )}
              {hasFilters && (
                <Button variant="ghost" size="sm" className="dls-h-6 dls-text-xs" onClick={clearFilters}>
                  Clear all
                </Button>
              )}
              <div className="dls-relative">
                <Search className="dls-absolute dls-left-3 dls-top-1/2 -dls-translate-y-1/2 dls-h-4 dls-w-4 dls-text-muted-foreground" />
                <Input
                  placeholder="Search any field..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="dls-pl-9 dls-w-full sm:dls-w-64 dls-rounded-xl"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className={`dls-p-0 ${!usersLoading && users.length > 0 ? 'dls-overflow-x-auto' : ''}`}>
            <Table className={!usersLoading && users.length > 0 ? 'dls-min-w-[900px]' : ''}>
              <TableHeader>
                <TableRow>
                  <SortableHeader field="name">Name</SortableHeader>
                  <SortableHeader field="email">Email</SortableHeader>

                  {/* Role — sortable + filterable */}
                  <TableHead>
                    <div className="dls-flex dls-items-center dls-gap-1">
                      <button
                        type="button"
                        onClick={() => handleSort('role')}
                        className="dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors -dls-ml-1 dls-px-1 dls-py-0.5 dls-rounded"
                      >
                        Role
                        <SortIcon field="role" />
                      </button>
                      <Select
                        value={roleFilter || 'all'}
                        onValueChange={(v) => handleFilterChange(setRoleFilter)(v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                          <span className="dls-sr-only">Filter role</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Roles</SelectItem>
                          {availableRoles.map((r) => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableHead>

                  <SortableHeader field="lastLogin">Last Login</SortableHeader>
                  <SortableHeader field="createdAt">Created</SortableHeader>

                  {/* Status — sortable + filterable */}
                  <TableHead>
                    <div className="dls-flex dls-items-center dls-gap-1">
                      <button
                        type="button"
                        onClick={() => handleSort('enabled')}
                        className="dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors -dls-ml-1 dls-px-1 dls-py-0.5 dls-rounded"
                      >
                        Status
                        <SortIcon field="enabled" />
                      </button>
                      <Select
                        value={enabledFilter || 'all'}
                        onValueChange={(v) => handleFilterChange(setEnabledFilter)(v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                          <span className="dls-sr-only">Filter status</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="true">Enabled</SelectItem>
                          <SelectItem value="false">Disabled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableHead>

                  {/* Flags — filterable only (not sortable) */}
                  <TableHead>
                    <div className="dls-flex dls-items-center dls-gap-1">
                      <span className="-dls-ml-1 dls-px-1">Flags</span>
                      <Select
                        value={flagFilter || 'all'}
                        onValueChange={(v) => handleFilterChange(setFlagFilter)(v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                          <span className="dls-sr-only">Filter flags</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Flags</SelectItem>
                          {availableFlags.map((f) => (
                            <SelectItem key={f} value={f}>{f}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usersLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="dls-text-center dls-py-12">
                      <div className="dls-inline-block dls-animate-spin dls-rounded-full dls-h-6 dls-w-6 dls-border-b-2 dls-border-primary" />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="dls-text-center dls-py-12 dls-text-sm dls-text-muted-foreground">
                      {hasFilters
                        ? 'No users found matching the current filters.'
                        : 'No users have been discovered yet.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow
                      key={user.id}
                      className="hover:dls-bg-muted/50 dls-transition-colors dls-cursor-pointer"
                      onClick={() => setSelectedUserId(user.id)}
                    >
                      <TableCell className="dls-font-medium">{user.name}</TableCell>
                      <TableCell className="dls-text-muted-foreground dls-text-sm">
                        {user.email}
                      </TableCell>
                      <TableCell>
                        {user.role ? (
                          <span className="dls-inline-flex dls-items-center dls-rounded-full dls-bg-secondary dls-text-secondary-foreground dls-px-2 dls-py-0.5 dls-text-xs dls-font-medium">
                            {user.role}
                          </span>
                        ) : (
                          '\u2014'
                        )}
                      </TableCell>
                      <TableCell className="dls-text-muted-foreground dls-text-sm">
                        {timeAgo(user.lastLogin)}
                      </TableCell>
                      <TableCell className="dls-text-muted-foreground dls-text-sm">
                        {formatDate(user.createdAt)}
                      </TableCell>
                      <TableCell>
                        {user.enabled === true ? (
                          <span className="dls-inline-flex dls-items-center dls-rounded-full dls-bg-status-online-quarternary dls-text-status-online dls-px-2 dls-py-0.5 dls-text-xs dls-font-medium">
                            Enabled
                          </span>
                        ) : user.enabled === false ? (
                          <span className="dls-inline-flex dls-items-center dls-rounded-full dls-bg-status-offline-quarternary dls-text-status-offline dls-px-2 dls-py-0.5 dls-text-xs dls-font-medium">
                            Disabled
                          </span>
                        ) : (
                          '\u2014'
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="dls-flex dls-flex-wrap dls-gap-1">
                          {user.flags.length > 0
                            ? user.flags.map((flag) => <FlagBadge key={flag} flag={flag} />)
                            : '\u2014'}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

          <TablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
          />
        </CardContent>
      </Card>
      </div>

      {/* User Detail Drawer */}
      {selectedUserId && (
        <UserDetailDrawer
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          roles={roles}
        />
      )}
    </div>
  );
}
